import { isProductInStock } from "./productAvailability";
import { getProductRating, getProductSalesCount } from "./productSorting";

const PRIMARY_RELATED_KEYS = [
  "kind",
  "type",
  "subcategory",
  "species",
  "animalType",
  "petType",
  "productType",
  "breed",
  "forBreed",
];

const SECONDARY_RELATED_KEYS = ["purpose"];
const CATEGORY_KEYS = ["category"];

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  return value != null && value !== "" ? [value] : [];
};

const normalizeToken = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getTokenSet = (product, keys) => {
  const tokens = new Set();

  keys.forEach((key) => {
    toArray(product?.[key])
      .map(normalizeToken)
      .filter(Boolean)
      .forEach((token) => tokens.add(token));
  });

  return tokens;
};

const hasTokenIntersection = (left, right) => {
  for (const token of left) {
    if (right.has(token)) return true;
  }
  return false;
};

const compareCandidates = (left, right) => {
  if (left.matchRank !== right.matchRank) {
    return right.matchRank - left.matchRank;
  }

  if (left.inStock !== right.inStock) {
    return Number(right.inStock) - Number(left.inStock);
  }

  if (left.salesCount !== right.salesCount) {
    return right.salesCount - left.salesCount;
  }

  if (left.rating !== right.rating) {
    return right.rating - left.rating;
  }

  return 0;
};

export const pickRelatedProducts = (
  currentProduct,
  relatedSource,
  limit = 4,
) => {
  if (!currentProduct || !Array.isArray(relatedSource) || limit <= 0) {
    return [];
  }

  const currentCategoryTokens = getTokenSet(currentProduct, CATEGORY_KEYS);
  const currentPrimaryTokens = getTokenSet(currentProduct, PRIMARY_RELATED_KEYS);
  const currentSecondaryTokens = getTokenSet(
    currentProduct,
    SECONDARY_RELATED_KEYS,
  );
  const currentManufacturer = normalizeToken(currentProduct?.manufacturer);

  const seen = new Set();

  return relatedSource
    .filter((candidate) => {
      if (!candidate) return false;
      if (candidate._id === currentProduct._id) return false;
      if (
        currentProduct.productCode != null &&
        candidate.productCode === currentProduct.productCode
      ) {
        return false;
      }
      if (
        currentProduct.productGroupId &&
        candidate.productGroupId === currentProduct.productGroupId
      ) {
        return false;
      }

      const dedupeKey =
        candidate.productGroupId ||
        candidate.productCode ||
        candidate._id ||
        `${candidate.name}-${candidate.weight}`;

      if (!dedupeKey || seen.has(dedupeKey)) {
        return false;
      }

      seen.add(dedupeKey);
      return true;
    })
    .map((candidate) => {
      const categoryTokens = getTokenSet(candidate, CATEGORY_KEYS);
      const primaryTokens = getTokenSet(candidate, PRIMARY_RELATED_KEYS);
      const secondaryTokens = getTokenSet(candidate, SECONDARY_RELATED_KEYS);
      const sameCategory = hasTokenIntersection(
        currentCategoryTokens,
        categoryTokens,
      );
      const samePrimary = hasTokenIntersection(
        currentPrimaryTokens,
        primaryTokens,
      );
      const sameSecondary = hasTokenIntersection(
        currentSecondaryTokens,
        secondaryTokens,
      );
      const sameManufacturer =
        currentManufacturer &&
        currentManufacturer === normalizeToken(candidate?.manufacturer);

      let matchRank = 0;

      if (sameCategory && samePrimary) {
        matchRank = 4;
      } else if (sameCategory && sameSecondary) {
        matchRank = 3;
      } else if (sameCategory) {
        matchRank = 2;
      } else if (samePrimary || sameManufacturer) {
        matchRank = 1;
      }

      return {
        product: candidate,
        matchRank,
        inStock: isProductInStock(candidate),
        rating: getProductRating(candidate),
        salesCount: getProductSalesCount(candidate),
      };
    })
    .filter((candidate) => candidate.matchRank > 0)
    .sort(compareCandidates)
    .slice(0, limit)
    .map(({ product }) => product);
};
